import React, { Fragment, useCallback, useEffect, useState } from 'react';

import { App, Button, Divider, Modal, Tag, Typography } from 'antd';

import Spin from 'components/Spin';
import {
  useProductCampaignTracker,
  useCreateOrUpdateCampaignTracker,
  useDeleteCampaignTracker
} from 'v2/hooks/products/useProductCampaignTracker';

import { Icon } from '@eduzz/ui-antd-theme';

import Advanced from './components/Advanced';
import Basic from './components/Basic';
import Platforms from './components/Platforms';
import type { enTypePlatform } from './components/Platforms';
import TagmanagerAlert from './components/TagmanagerAlert';

interface IProps {
  open: boolean;
  handleClose: () => void;
  product: {
    id: number;
  };
  campaign: {
    id: number;
  };
}

interface ITrackerState {
  id: string;
  step: number;
  advanced: boolean;
  platformId: number;
  platformType: enTypePlatform;
  hasCampaign: boolean;
  hasPrice: boolean;
  hasBankslipPurchase: boolean;
  bankslipPurchase: string;
  tokenApi: string;
  pixel: string;
  registered: string[];
  isFetching: boolean;
  tracker: any;
  pages: string[];
  paymentMethods: {
    credit_card: number;
    bankslip: number;
    paypal: number;
    pix: number;
  };
  purchaseValue: string;
  sendCampaign: boolean;
  conversionLabel: string;
}

const INITIAL_STATE: ITrackerState = {
  id: '',
  step: 1,
  platformId: null,
  platformType: null,
  pages: ['checkout', 'thank_you_card', 'thank_you_bankslip', 'bankslip_view'],
  sendCampaign: true,
  advanced: false,
  pixel: '',
  registered: [],
  hasCampaign: true,
  hasPrice: true,
  hasBankslipPurchase: false,
  tokenApi: null,
  bankslipPurchase: 'send_purchase',
  isFetching: false,
  tracker: null,
  purchaseValue: 'send_purchase_value',
  conversionLabel: '',
  paymentMethods: {
    credit_card: 0,
    bankslip: 0,
    paypal: 0,
    pix: 0
  }
};

const TrackerForm2: React.FC<IProps> = ({ open, handleClose, product }) => {
  const { message } = App.useApp();
  const [state, setState] = useState<ITrackerState>(INITIAL_STATE);

  const { trackerData, isFetching: isLoadingTracker, refetch: refetchTracker } = useProductCampaignTracker(product.id);
  const createTrackerMutation = useCreateOrUpdateCampaignTracker();
  const deleteTrackerMutation = useDeleteCampaignTracker();

  useEffect(() => {
    if (trackerData) {
      const registered = trackerData.pixel ? trackerData.pixel.map((item: any): string[] => item.type) : [];
      setState(prev => ({
        ...prev,
        isFetching: false,
        tracker: trackerData,
        registered
      }));
    }
  }, [trackerData]);

  const handlePageChange = useCallback(
    (name: string) => () => {
      setState(prev => {
        const pages = [...prev.pages];
        const index = pages.findIndex(k => k === name);

        if (index === -1) {
          pages.push(name);
        } else if (pages.length > 1) {
          pages.splice(index, 1);
        }

        return { ...prev, pages };
      });
    },
    []
  );

  const handleRadioChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, purchaseValue: event.target.value }));
  }, []);

  const handleAdvanced = useCallback(() => {
    setState(prev => ({ ...prev, advanced: !prev.advanced }));
  }, []);

  const handlePixel = useCallback((pixel: string) => {
    setState(prev => ({ ...prev, pixel }));
  }, []);

  const handleTokenApi = useCallback((token: string) => {
    setState(prev => ({ ...prev, tokenApi: token.replace(/ /g, '') }));
  }, []);

  const handleConversionLabel = useCallback((conversionLabel: string) => {
    setState(prev => ({ ...prev, conversionLabel }));
  }, []);

  const handleBack = useCallback(() => {
    setState(prev => ({ ...prev, step: 1 }));
  }, []);

  const handlePlatform = useCallback(
    (
      platformId: number,
      platformType: enTypePlatform,
      hasCampaign: boolean,
      hasPrice: boolean,
      hasBankslipPurchase: boolean,
      step: number
    ) => {
      setState(prev => {
        const platform = prev.tracker?.pixel?.find((x: any) => x.type === platformType);

        if (platform) {
          let purchaseValue = 'never';

          if (platform.hasPurchaseValue) {
            purchaseValue = 'send_purchase_value';
          }

          if (platform.hasCustomValues) {
            purchaseValue = 'custom_values';
          }

          return {
            ...prev,
            step,
            platformType,
            platformId,
            hasCampaign,
            hasBankslipPurchase,
            hasPrice,
            id: prev.tracker.id,
            pixel: platform.pixelId,
            pages: platform.enabledPages,
            paymentMethods: platform.forcePaymentValues,
            purchaseValue,
            bankslipPurchase: 'send_purchase',
            conversionLabel: platform.conversionLabel,
            tokenApi: platform.tokenApi
          };
        }

        return {
          ...prev,
          step,
          platformType,
          platformId,
          hasCampaign,
          hasBankslipPurchase,
          hasPrice,
          id: '',
          pixel: '',
          conversionLabel: '',
          pages: ['checkout', 'thank_you_card', 'thank_you_bankslip', 'bankslip_view'],
          paymentMethods: { credit_card: 0, bankslip: 0, paypal: 0, pix: 0 },
          purchaseValue: 'send_purchase_value',
          bankslipPurchase: 'send_purchase'
        };
      });
    },
    []
  );

  const checkCustomValue = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => {
      const paymentMethods = { ...prev.paymentMethods };
      const key = event.target.value as keyof ITrackerState['paymentMethods'];
      paymentMethods[key] = paymentMethods[key] > 0 ? 0 : 1;
      return { ...prev, paymentMethods };
    });
  }, []);

  const handleCustomValue = useCallback((value: any, number: any) => {
    setState(prev => {
      const paymentMethods = { ...prev.paymentMethods };
      const key = value as keyof ITrackerState['paymentMethods'];
      paymentMethods[key] = Number(number.replace(',', '.'));
      return { ...prev, paymentMethods };
    });
  }, []);

  const handleSendCampaign = useCallback(() => {
    setState(prev => ({ ...prev, sendCampaign: !prev.sendCampaign }));
  }, []);

  const handleBankslipPurchase = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({ ...prev, bankslipPurchase: event.target.value }));
  }, []);

  const handleDelete = useCallback(
    async (idPixel: any, type: string) => {
      try {
        setState(prev => ({ ...prev, isFetching: true }));
        await deleteTrackerMutation.mutateAsync({ id: idPixel, type });
        await refetchTracker();
        message.success('Pixel removido com sucesso!');
        setState(prev => ({ ...prev, isFetching: false, step: 1 }));
      } catch (error) {
        message.error(error instanceof Error ? error.message : 'Não foi possível remover o pixel');
      }
    },
    [deleteTrackerMutation, message, refetchTracker]
  );

  const handleSubmit = useCallback(async () => {
    const tracker: any = {
      type: state.platformType,
      pixelId: state.pixel,
      enabledPages: state.pages,
      forcePaymentValues: state.paymentMethods,
      tokenApi: state.tokenApi,
      contentId: product.id,
      hasCampaign: state.sendCampaign,
      hasPurchaseValue: true,
      conversionLabel: state.conversionLabel,
      bankslipPurchase: state.bankslipPurchase
    };

    if (state.purchaseValue === 'custom_values') {
      tracker.hasCustomValues = true;
    }

    if (state.purchaseValue === 'send_purchase_value') {
      tracker.hasCustomValues = false;
    }

    if (state.purchaseValue === 'never') {
      tracker.hasPurchaseValue = false;
    }

    try {
      setState(prev => ({ ...prev, isFetching: true }));
      await createTrackerMutation.mutateAsync({ tracker, id: state.id });
      await refetchTracker();
      message.success('Pixel cadastrado com sucesso!');
      setState(prev => ({ ...prev, isFetching: false, step: 1 }));
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Erro ao salvar o pixel');
      setState(prev => ({ ...prev, isFetching: false }));
    }
  }, [state, product.id, createTrackerMutation, message, refetchTracker]);

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      confirmLoading={state.isFetching || isLoadingTracker}
      title='Pixel de Rastreamento'
      okText='Salvar'
      cancelText='Fechar'
      cancelButtonProps={{ type: 'text' }}
      onOk={handleSubmit}
      okButtonProps={{ className: state.step !== 2 ? 'hidden' : undefined }}
    >
      <Typography.Paragraph type='secondary'>Selecione a plataforma que deseja cadastrar o pixel:</Typography.Paragraph>
      {state.step === 1 && (
        <Fragment>
          {state.isFetching ? (
            <div className='flex items-center justify-center p-8'>
              <Spin />
            </div>
          ) : (
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              {Platforms.map((item, index) => (
                <div key={`platform-${index}`}>
                  <Button
                    key={`platform-${index}`}
                    block
                    className='relative h-auto py-8'
                    onClick={() =>
                      handlePlatform(item.id, item.type, item.hasCampaign, item.hasPrice, item.hasBankslipPurchase, 2)
                    }
                  >
                    {state.registered.includes(item.type) && (
                      <Tag color='success' className='absolute top-1 right-1'>
                        <Icon.Check size={16} />
                      </Tag>
                    )}
                    <img src={item.image} alt={item.label} className='max-h-9 max-w-full' />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Fragment>
      )}
      {state.step === 2 && (
        <div className='mr-4'>
          <TagmanagerAlert type={state.platformType} pixels={state.tracker?.pixel || []} />

          <Basic
            platformType={state.platformType}
            conversionLabel={state.conversionLabel}
            loading={state.isFetching}
            pixel={state.pixel}
            tokenApi={state.tokenApi}
            pixelId={state.id}
            handleTokenApi={handleTokenApi}
            back={handleBack}
            handleConversionLabel={handleConversionLabel}
            handlePixel={handlePixel}
            handleDelete={handleDelete}
          />

          <Typography.Link onClick={handleAdvanced}>
            {'Configurações adicionais  '}
            {state.advanced ? <Icon.ChevronUp size={16} /> : <Icon.ChevronDown size={16} />}
          </Typography.Link>
          <div className='mt-6' />
          <Divider />
          <Advanced
            handlePageChange={handlePageChange}
            handleSendCampaign={handleSendCampaign}
            handleRadioChange={handleRadioChange}
            handleCustomValue={handleCustomValue}
            handleBankslipPurchase={handleBankslipPurchase}
            checkCustomValue={checkCustomValue}
            state={state}
          />
        </div>
      )}
    </Modal>
  );
};

export default TrackerForm2;
